import {
  Availability_Status_Enum,
  Base_Unit_Insert_Input,
  Base_Unit_Type_Enum,
  Duration_Type_Enum,
  Gtin_Type_Enum,
  Measurement_Unit_Enum,
  Packaging_Insert_Input,
  Preservation_Method_Enum,
  Product_Insert_Input,
  Product_Label_Name_Enum,
  Trade_Item_Unit_Descriptor_Enum,
} from '@kheops/graphql';

export interface ProductPhotoSource {
  path: string;
  domain?: string | null;
}

export interface PackagingInsertSource {
  sku: number;
  trade_item_unit_descriptor: Trade_Item_Unit_Descriptor_Enum;
  unit_quantity?: number | null;
  net_content?: number | null;
  content_measurement_unit?: Measurement_Unit_Enum | null;
  price: number;
}

export interface BaseUnitInsertSource {
  sku?: number | null;
  availability: Availability_Status_Enum;
  content_measurement_unit?: Measurement_Unit_Enum | null;
  gtin?: string | null;
  gtin_type?: Gtin_Type_Enum | null;
  unit_net_content?: number | null;
  unit_quantity?: number | null;
  unit_type?: Base_Unit_Type_Enum | null;
  suggested_retail_price?: number | null;
  main_photo?: ProductPhotoSource | null;
  photos?: { photo: ProductPhotoSource }[] | null;
  packagings: PackagingInsertSource[];
}

export interface ProductInsertSource {
  sku: number;
  company_id: string;
  name: string;
  description?: string | null;
  shelf_life_duration?: number | null;
  shelf_life_duration_type?: Duration_Type_Enum | null;
  sub_family: string;
  vat_rate?: number | null;
  ingredients?: string | null;
  allergens?: string | null;
  calories?: number | null;
  calories_kj?: number | null;
  fat?: number | null;
  saturated_fat?: number | null;
  carbohydrate?: number | null;
  sugars?: number | null;
  sodium?: number | null;
  protein?: number | null;
  brand?: string | null;
  alcohol_by_volume?: number | null;
  is_exempt_from_ingredient_list?: boolean | null;
  has_no_allergen_declaration?: boolean | null;
  preservation_method?: Preservation_Method_Enum | null;
  generic_product_code?: string | null;
  generic_product_level?: string | null;
  labels: { label: Product_Label_Name_Enum }[];
  classification_tags: { tag_code: string }[];
  base_units: BaseUnitInsertSource[];
}

export function convertPackagingsToInsertInput(packagings: PackagingInsertSource[], companyId: string): Packaging_Insert_Input[] {
  return packagings.map((packaging) => ({
    company_id: companyId,
    sku: packaging.sku,
    trade_item_unit_descriptor: packaging.trade_item_unit_descriptor,
    unit_quantity: packaging.unit_quantity,
    net_content: packaging.net_content,
    content_measurement_unit: packaging.content_measurement_unit,
    price: packaging.price,
  }));
}

export function convertBaseUnitsToInsertInput(baseUnits: BaseUnitInsertSource[], companyId: string): Base_Unit_Insert_Input[] {
  return baseUnits.map((baseUnit) => {
    const unwrappedPhotos = (baseUnit.photos ?? []).map(({ photo }) => photo);
    const mainPhoto = baseUnit.main_photo;
    const main_photo = mainPhoto
      ? { data: { path: mainPhoto.path, domain: mainPhoto.domain } }
      : undefined;
    const photos = unwrappedPhotos.length
      ? {
        data: unwrappedPhotos
          .sort((photo) => (photo.path === mainPhoto?.path ? -1 : 0))
          .map((photo) => ({
            photo: {
              data: {
                domain: photo.domain,
                path: photo.path,
              },
            },
          })),
      }
      : undefined;

    return {
      company_id: companyId,
      sku: baseUnit.sku,
      availability: baseUnit.availability,
      content_measurement_unit: baseUnit.content_measurement_unit,
      gtin: baseUnit.gtin,
      gtin_type: baseUnit.gtin_type,
      unit_net_content: baseUnit.unit_net_content,
      unit_quantity: baseUnit.unit_quantity,
      unit_type: baseUnit.unit_type,
      main_photo,
      photos,
      suggested_retail_price: baseUnit.suggested_retail_price,
      packagings: {
        data: convertPackagingsToInsertInput(baseUnit.packagings, companyId),
      },
    };
  });
}

export function convertProductToInsertInput(product: ProductInsertSource): Product_Insert_Input {
  return {
    sku: product.sku,
    company_id: product.company_id,
    name: product.name,
    description: product.description,
    shelf_life_duration: product.shelf_life_duration,
    shelf_life_duration_type: product.shelf_life_duration_type,
    sub_family: product.sub_family,
    vat_rate: product.vat_rate,
    labels: {
      data: product.labels.map((productLabel) => ({ label: productLabel.label })),
    },
    classification_tags: {
      data: product.classification_tags.map((assignment) => ({ tag_code: assignment.tag_code })),
    },
    base_units: {
      data: convertBaseUnitsToInsertInput(product.base_units, product.company_id),
    },
    ingredients: product.ingredients,
    allergens: product.allergens,
    calories: product.calories,
    calories_kj: product.calories_kj,
    fat: product.fat,
    saturated_fat: product.saturated_fat,
    carbohydrate: product.carbohydrate,
    sugars: product.sugars,
    sodium: product.sodium,
    protein: product.protein,
    brand: product.brand,
    alcohol_by_volume: product.alcohol_by_volume,
    is_exempt_from_ingredient_list: product.is_exempt_from_ingredient_list,
    has_no_allergen_declaration: product.has_no_allergen_declaration,
    preservation_method: product.preservation_method,
    generic_product_code: product.generic_product_code,
    generic_product_level: product.generic_product_level,
  };
}
